import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  Typography,
} from '@material-ui/core'
import { StarIcon } from 'app/components/icons'
import Assets from 'app/configs/assetConfigs'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import Button from './Button'
import GenerateError from './GenerateError'

export default function RateWithAIDialog({
  isOpen,
  status,
  rating,
  feedback,
  revisedVersion,
  credits,
  onCloseClick,
  onCancelClick,
  onErrorRetry,
  onReplaceWithAIVersionClick,
}) {
  if (!isOpen) return null

  function renderContentByStatus() {
    switch (status) {
      case 'rated': {
        return (
          <div className="space-y-20">
            {feedback && (
              <div className="space-y-4">
                <Typography
                  variant="subtitle2"
                  className="w-full text-gray-800"
                >
                  <FormattedMessage id="ai.dialog.rate.feedback.title" />
                </Typography>
                <div className="w-full">{feedback}</div>
              </div>
            )}
            {revisedVersion && (
              <React.Fragment>
                <div className="space-y-16">
                  <Typography
                    variant="subtitle2"
                    className="w-full text-gray-800"
                  >
                    <FormattedMessage id="ai.dialog.rate.revisedVersion.title" />
                  </Typography>
                  <div
                    className="w-full bg-primary-200 p-16 h-288 overflow-y-auto outline-none rounded-5 ai-text-preview"
                    dangerouslySetInnerHTML={{ __html: revisedVersion }}
                  />
                </div>

                <div className="flex items-center space-x-16">
                  <Button
                    onClick={() => {
                      onCloseClick()
                    }}
                    variant="secondary"
                    className="h-40"
                  >
                    <div className="flex items-center space-x-16">
                      <Typography variant="subtitle2">
                        <FormattedMessage id="ai.dialog.discardAndClose.button" />
                      </Typography>
                    </div>
                  </Button>

                  <Button
                    onClick={() => {
                      onReplaceWithAIVersionClick()
                    }}
                    className="h-40"
                    variant="primary"
                  >
                    <div className="flex items-center space-x-16 text-white">
                      <StarIcon className="w-24 h-24" />
                      <span>
                        <FormattedMessage id="ai.dialog.useContent" />
                      </span>
                    </div>
                  </Button>
                </div>
              </React.Fragment>
            )}
          </div>
        )
      }

      case 'loading': {
        return (
          <div className="space-y-20">
            <div className="py-20 px-16 text-center">
              <img
                src={Assets.AILoading}
                width={120}
                height={120}
                alt="Loading"
              />
            </div>

            <Button
              onClick={() => {
                onCancelClick()
              }}
              className="flex justify-center py-8"
              variant="secondary"
            >
              <Typography variant="subtitle2" className="text-gray-700">
                <FormattedMessage id="ai.dialog.rate.cancel" />
              </Typography>
            </Button>
          </div>
        )
      }

      case 'regenerating': {
        return (
          <div className="space-y-20">
            <div className="py-20 px-16 text-center">
              <img
                src={Assets.AILoading}
                width={120}
                height={120}
                alt="Loading"
              />
            </div>

            <Button
              onClick={() => {
                onCloseClick()
              }}
              className="flex justify-center py-8"
              variant="secondary"
            >
              <Typography variant="subtitle2" className="text-gray-700">
                <FormattedMessage id="cancelBtn" />
              </Typography>
            </Button>
          </div>
        )
      }

      case 'error': {
        return (
          <div className="space-y-8 text-center">
            <GenerateError onRetry={onErrorRetry} />
          </div>
        )
      }

      default: {
        return null
      }
    }
  }

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex items-center space-x-8 justify-between">
          <span className="flex items-center space-x-8">
            {status === 'rated' && rating !== undefined && (
              <div className="flex flex-col">
                <span className="text-18 text-gray-600">
                  <FormattedMessage id="ai.dialog.rate.title" />
                </span>
                <div className="text-44 font-bold text-gray-600">
                  <FormattedMessage
                    id="ai.dialog.rate.rating"
                    values={{ rating }}
                  />
                </div>
              </div>
            )}
          </span>

          <div className="flex items-center">
            <span className="pl-16 pr-20 py-8 rounded-full flex items-center space-x-6 bg-orange-500 text-base text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-24 w-24"
                viewBox="0 0 24 25"
              >
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10.773 20.864C6.48 20.864 3 17.2 3 12.682S6.48 4.5 10.773 4.5c4.292 0 7.772 3.663 7.772 8.182s-3.48 8.182-7.772 8.182z"
                ></path>
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12.818 10.064a2.932 2.932 0 00-1.84-.655c-1.695 0-3.069 1.465-3.069 3.273s1.374 3.272 3.068 3.272c.69 0 1.328-.243 1.841-.654"
                ></path>
                <path
                  stroke="#fff"
                  strokeWidth="2"
                  d="M10.364 4.5C13.909 4.5 21 5.318 21 12.682c0 7.364-7.091 8.182-10.636 8.182"
                ></path>
              </svg>

              <span>{credits ? credits.toLocaleString() : '-'}</span>
            </span>
            <Divider
              className="min-h-40 ml-16"
              variant="inset"
              orientation="vertical"
            />
            <IconButton
              aria-label="close"
              onClick={onCloseClick}
              className="ml-6"
            >
              <Icon className="text-20">close</Icon>
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent className="pb-20">{renderContentByStatus()}</DialogContent>
    </Dialog>
  )
}
